import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../../components/navbar';
import toast from 'react-hot-toast';
import { FaPlus, FaEdit, FaTrash, FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';

function DropdownConfig() {
  const [dropdownItems, setDropdownItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [newItem, setNewItem] = useState({
    sectionName: '',
    mainHeading: '',
    subHeading: '',
    dropdownOptions: []
  });
  const [newOption, setNewOption] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [filterSection, setFilterSection] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingOption, setEditingOption] = useState('');
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch all dropdown configurations
  useEffect(() => {
    const fetchDropdownItems = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/dropdown/getAll`);
        setDropdownItems(response.data);
      } catch (err) {
        setError('Error fetching dropdown configurations');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDropdownItems();
  }, []);

  // Get unique sections for filter dropdown
  const uniqueSections = [...new Set(dropdownItems
    .map(item => item.sectionName)
    .filter(name => name !== null && name !== undefined)
  )];

  // Filter items by section and search term
  const filteredItems = dropdownItems.filter(item => {
    // Apply section filter
    if (filterSection !== 'all' && item.sectionName !== filterSection) {
      return false;
    }
    
    // Apply search filter if search term exists
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      
      // Check if any field matches the search term
      const matchesSection = item.sectionName && item.sectionName.toLowerCase().includes(searchLower);
      const matchesMain = item.mainHeading && item.mainHeading.toLowerCase().includes(searchLower);
      const matchesSub = item.subHeading && item.subHeading.toLowerCase().includes(searchLower);
      const matchesOptions = item.dropdownOptions && 
        item.dropdownOptions.some(option => 
          option && option.toString().toLowerCase().includes(searchLower)
        );
      
      return matchesSection || matchesMain || matchesSub || matchesOptions;
    }
    
    return true;
  });

  // Toggle expansion of item details
  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Handle input changes for create/edit forms
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle empty strings as null
    const processedValue = value.trim() === '' ? null : value;
    
    if (editModalOpen && currentItem) {
      setCurrentItem(prev => ({
        ...prev,
        [name]: processedValue
      }));
    } else {
      setNewItem(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }
  };

  // Add new option to new item
  const handleAddOption = () => {
    if (newOption.trim()) {
      setNewItem(prev => ({
        ...prev,
        dropdownOptions: [...prev.dropdownOptions, newOption.trim()]
      }));
      setNewOption('');
    }
  };

  // Remove option from new item
  const handleRemoveOption = (index) => {
    setNewItem(prev => {
      const newOptions = [...prev.dropdownOptions];
      newOptions.splice(index, 1);
      return {
        ...prev,
        dropdownOptions: newOptions
      };
    });
  };

  // Create new dropdown configuration
  const handleCreateItem = async () => {
    try {
      // Prepare data with null-safe values
      const payload = {
        ...newItem,
        sectionName: newItem.sectionName || null,
        mainHeading: newItem.mainHeading || null,
        subHeading: newItem.subHeading || null,
        dropdownOptions: newItem.dropdownOptions.length > 0 
          ? newItem.dropdownOptions 
          : []
      };

      const response = await axios.post(`${API_URL}/api/dropdown/create`, payload);
      setDropdownItems(prev => [...prev, response.data]);
      setCreateModalOpen(false);
      setNewItem({
        sectionName: '',
        mainHeading: '',
        subHeading: '',
        dropdownOptions: []
      });
      toast.success('Dropdown configuration created successfully');
    } catch (err) {
      console.error('Create error:', err);
      toast.error('Error creating dropdown configuration');
    }
  };

  // Update existing dropdown configuration
  const handleUpdateItem = async () => {
    try {
      // Prepare data with null-safe values
      const payload = {
        ...currentItem,
        sectionName: currentItem.sectionName || null,
        mainHeading: currentItem.mainHeading || null,
        subHeading: currentItem.subHeading || null,
        dropdownOptions: currentItem.dropdownOptions || []
      };

      const response = await axios.patch(
        `${API_URL}/api/dropdown/${currentItem._id}/update`,
        payload
      );
      
      setDropdownItems(prev => 
        prev.map(item => 
          item._id === currentItem._id ? response.data : item
        )
      );
      
      setEditModalOpen(false);
      setCurrentItem(null);
      toast.success('Dropdown configuration updated successfully');
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Error updating dropdown configuration');
    }
  };

  // Delete dropdown configuration
  const handleDeleteItem = async () => {
    try {
      await axios.delete(`${API_URL}/api/dropdown/${currentItem._id}`);
      setDropdownItems(prev => prev.filter(item => item._id !== currentItem._id));
      setDeleteModalOpen(false);
      setCurrentItem(null);
      toast.success('Dropdown configuration deleted successfully');
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Error deleting dropdown configuration');
    }
  };

  // Format value for display (handle null/undefined)
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (Array.isArray(value) && value.length === 0) return 'No options';
    return value;
  };

  // Format dropdown options for display
  const formatOptions = (options) => {
    if (!options || options.length === 0) {
      return <div className="text-gray-500 italic text-center py-2">No options configured</div>;
    }
    
    return options.map((option, index) => {
      if (option === null || option === undefined) {
        return <div key={index} className="bg-gray-100 p-2 rounded mb-2 italic">Null option</div>;
      }
      
      if (typeof option === 'object') {
        return (
          <div key={index} className="bg-gray-100 p-2 rounded mb-2">
            <pre>{JSON.stringify(option, null, 2)}</pre>
          </div>
        );
      }
      
      return <div key={index} className="bg-gray-100 p-2 rounded mb-2">{option}</div>;
    });
  };

  // Handle adding/editing options in edit modal
  const handleEditOption = (e) => {
    setEditingOption(e.target.value);
  };

  // Add new option in edit modal
  const handleAddEditOption = () => {
    if (editingOption.trim()) {
      const updatedOptions = [...currentItem.dropdownOptions, editingOption.trim()];
      setCurrentItem(prev => ({
        ...prev,
        dropdownOptions: updatedOptions
      }));
      setEditingOption('');
    }
  };

  // Start editing an existing option
  const handleStartEditingOption = (index, value) => {
    setEditingOptionIndex(index);
    setEditingOption(value);
  };

  // Save edited option
  const handleSaveEditOption = () => {
    if (editingOption.trim() && editingOptionIndex !== null) {
      const updatedOptions = [...currentItem.dropdownOptions];
      updatedOptions[editingOptionIndex] = editingOption.trim();
      
      setCurrentItem(prev => ({
        ...prev,
        dropdownOptions: updatedOptions
      }));
      
      setEditingOptionIndex(null);
      setEditingOption('');
    }
  };

  // Cancel editing an option
  const handleCancelEditOption = () => {
    setEditingOptionIndex(null);
    setEditingOption('');
  };

  // Remove option in edit modal
  const handleRemoveEditOption = (index) => {
    const updatedOptions = [...currentItem.dropdownOptions];
    updatedOptions.splice(index, 1);
    
    setCurrentItem(prev => ({
      ...prev,
      dropdownOptions: updatedOptions
    }));
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />
      <div className="p-6 pt-28 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h1 className="text-2xl font-bold mb-6">Dropdown Configurations</h1>
          
          <div className="flex flex-wrap gap-4 mb-6 w-full max-w-3xl">
            <div className="w-full md:w-auto">
              <label htmlFor="search" className="block mb-1 text-sm font-medium">Search:</label>
              <div className="relative">
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border rounded px-3 py-2 w-full md:w-64 pl-10"
                  placeholder="Search configurations..."
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>
            </div>
            
            <div className="w-full md:w-auto">
              <label htmlFor="filter" className="block mb-1 text-sm font-medium">Filter by Section:</label>
              <select 
                id="filter" 
                value={filterSection} 
                onChange={(e) => setFilterSection(e.target.value)}
                className="border rounded px-3 py-2 w-full md:w-48"
              >
                <option value="all">All Sections</option>
                {uniqueSections.map((section, index) => (
                  <option key={index} value={section}>{section || 'Uncategorized'}</option>
                ))}
              </select>
            </div>
            
            {/* <button
              onClick={() => setCreateModalOpen(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors mt-6 md:mt-7"
            >
              <FaPlus />
              Create New
            </button> */}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No dropdown configurations found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'No matches for your search' : 'Create a new configuration to get started'}
            </p>
          </div>
        )}

        <div className="space-y-4">
          {filteredItems.map(item => (
            <div key={item._id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center p-4 cursor-pointer" onClick={() => toggleExpand(item._id)}>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {formatValue(item.mainHeading) || 'Untitled Configuration'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {item.sectionName ? `Section: ${item.sectionName}` : 'No section'} • 
                    {item.subHeading ? `${item.subHeading}` : 'N/A'} • 
                    {item.dropdownOptions?.length || 0} options
                  </p>
                </div>
                <span className="text-gray-500">
                  {expandedItems[item._id] ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </div>            

              {expandedItems[item._id] && (
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-1">Section</h4>
                      <p className="text-gray-900">{formatValue(item.sectionName)}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-700 mb-1">Main Heading</h4>
                      <p className="text-gray-900">{formatValue(item.mainHeading)}</p>
                    </div>
                    
                    <div className="md:col-span-2">
                      <h4 className="font-medium text-gray-700 mb-1">Sub Heading</h4>
                      <p className="text-gray-900">{formatValue(item.subHeading)}</p>
                    </div>
                  </div>
                  
                  <h4 className="font-medium text-gray-700 mb-2">Dropdown Options</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {formatOptions(item.dropdownOptions)}
                  </div>
                  
                </div>
              )}

                  <div className="flex gap-3 mt-4 p-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setCurrentItem({...item});
                        setEditModalOpen(true);
                      }}
                      className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 transition-colors text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                    >
                      <FaEdit />
                      Edit
                    </button>
                    
                    <button
                      onClick={() => {
                        setCurrentItem({...item});
                        setDeleteModalOpen(true);
                      }}
                      className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <FaTrash />
                      Delete
                    </button>
                  </div>  

            </div>
          ))}
        </div>
      </div>

      {/* Create Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 flex items-center z-50 justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Create New Configuration</h3>
              <button 
                onClick={() => {
                  setCreateModalOpen(false);
                  setNewItem({
                    sectionName: '',
                    mainHeading: '',
                    subHeading: '',
                    dropdownOptions: []
                  });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium">Section Name</label>
                <input
                  type="text"
                  name="sectionName"
                  value={newItem.sectionName || ''}
                  onChange={handleInputChange}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Ex: Exterior, Interior, etc."
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank for no section</p>
              </div>
              
              <div>
                <label className="block mb-2 text-sm font-medium">Main Heading</label>
                <input
                  type="text"
                  name="mainHeading"
                  value={newItem.mainHeading || ''}
                  onChange={handleInputChange}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Ex: Tires, Engine, etc."
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank if not needed</p>
              </div>
              
              <div>
                <label className="block mb-2 text-sm font-medium">Sub Heading</label>
                <input
                  type="text"
                  name="subHeading"
                  value={newItem.subHeading || ''}
                  onChange={handleInputChange}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Optional description"
                />
                <p className="text-xs text-gray-500 mt-1">Optional field</p>
              </div>
              
              <div>
                <label className="block mb-2 text-sm font-medium">Dropdown Options</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    className="border rounded px-3 py-2 flex-grow"
                    placeholder="Add a new option"
                  />
                  <button
                    onClick={handleAddOption}
                    className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600"
                  >
                    Add
                  </button>
                </div>
                
                <div className="space-y-2 max-h-40 overflow-y-auto p-2 border rounded">
                  {newItem.dropdownOptions.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No options added yet</p>
                  ) : (
                    newItem.dropdownOptions.map((option, index) => (
                      <div key={index} className="flex justify-between items-center bg-gray-100 p-2 rounded">
                        <span>{option}</span>
                        <button 
                          onClick={() => handleRemoveOption(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Options are not required - leave empty for text input fields
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setCreateModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateItem}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Create Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && currentItem && (
        <div className="fixed inset-0 flex items-center z-50 justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Edit Configuration</h3>
              <button 
                onClick={() => setEditModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium">Section Name</label>
                <input
                  type="text"
                  name="sectionName"
                  value={currentItem.sectionName || ''}
                  onChange={handleInputChange}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Ex: Exterior, Interior, etc."
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank to remove section</p>
              </div>
              
              <div>
                <label className="block mb-2 text-sm font-medium">Main Heading</label>
                <input
                  type="text"
                  name="mainHeading"
                  value={currentItem.mainHeading || ''}
                  onChange={handleInputChange}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Ex: Tires, Engine, etc."
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank to remove heading</p>
              </div>
              
              <div>
                <label className="block mb-2 text-sm font-medium">Sub Heading</label>
                <input
                  type="text"
                  name="subHeading"
                  value={currentItem.subHeading || ''}
                  onChange={handleInputChange}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Optional description"
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">Optional field</p>
              </div>
              
              <div>
                <label className="block mb-2 text-sm font-medium">Dropdown Options</label>
                
                {/* Add new option */}
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={editingOption}
                    onChange={handleEditOption}
                    className="border rounded px-3 py-2 flex-grow"
                    placeholder="Add or edit an option"
                  />
                  {editingOptionIndex === null ? (
                    <button
                      onClick={handleAddEditOption}
                      className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600"
                    >
                      Add
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEditOption}
                        className="bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEditOption}
                        className="bg-gray-500 text-white px-3 py-2 rounded hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Display options with edit/remove */}
                <div className="space-y-2 max-h-60 overflow-y-auto p-2 border rounded">
                  {currentItem.dropdownOptions && currentItem.dropdownOptions.length > 0 ? (
                    currentItem.dropdownOptions.map((option, index) => (
                      <div key={index} className="flex justify-between items-center bg-gray-100 p-2 rounded">
                        {editingOptionIndex === index ? (
                          <input
                            type="text"
                            value={editingOption}
                            onChange={handleEditOption}
                            className="border-b border-gray-300 px-2 py-1 w-full"
                            autoFocus
                          />
                        ) : (
                          <span>{typeof option === 'object' ? JSON.stringify(option) : option}</span>
                        )}
                        
                        <div className="flex gap-2">
                          {editingOptionIndex !== index && (
                            <>
                              <button 
                                onClick={() => handleStartEditingOption(index, option)}
                                className="text-blue-500 hover:text-blue-700"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                onClick={() => handleRemoveEditOption(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">No options configured</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateItem}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && currentItem && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
            <p className="mb-4">
              Are you sure you want to delete this dropdown configuration?
            </p>
            <div className="bg-gray-100 p-4 rounded mb-6">
              <p><strong>Section:</strong> {formatValue(currentItem.sectionName)}</p>
              <p><strong>Main Heading:</strong> {formatValue(currentItem.mainHeading)}</p>
              <p><strong>Options:</strong> {currentItem.dropdownOptions?.length || 0}</p>
            </div>
            <p className="text-red-600 mb-6">
              This action cannot be undone and will permanently remove this configuration.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteItem}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DropdownConfig;